/**
 * Gallery Normalizer
 * Transforms gallery API responses to frontend models.
 * @module api/normalizers/gallery
 */

// LLM:METADATA
// :hierarchy: [Domain:Gallery | Data_Normalization | Service]
// :relates-to: used-by: "Gallery.vue"
// :rationale: "Extracts object-to-array normalization logic from Gallery component into dedicated normalizer layer for better separation of concerns."
// :contract: pre: "raw images data from API (array, object map, or invalid)", post: "always returns array of image objects", invariant: "output is always an array"
// :decision_cache: "Created dedicated normalizer to follow architectural requirement that normalization must live in normalization layer, not inside components [GALLERY-NORM-001]"
// LLM:END

/**
 * Normalize gallery images response
 * Transforms backend gallery images response to frontend model structure
 * Handles three possible response formats:
 * 1. Array of image objects -> return as-is
 * 2. Object map keyed by image_id -> return Object.values()
 * 3. Invalid/missing data -> return empty array
 *
 * @param {Array|Object|string} images - Raw images data from API
 * @returns {Array} Normalized array of image objects
 *
 * @example
 * // Array input (already normalized)
 * normalizeGalleryImages([{image_id: 1, img_path: '/a.jpg'}, {image_id: 2, img_path: '/b.jpg'}])
 * // => [{image_id: 1, img_path: '/a.jpg'}, {image_id: 2, img_path: '/b.jpg'}]
 *
 * @example
 * // Object map input (keyed by image_id)
 * normalizeGalleryImages({123: {image_id: 123, img_path: '/a.jpg'}, 456: {image_id: 456, img_path: '/b.jpg'}})
 * // => [{image_id: 123, img_path: '/a.jpg'}, {image_id: 456, img_path: '/b.jpg'}]
 *
 * @example
 * // Invalid input
 * normalizeGalleryImages(null)
 * // => []
 * normalizeGalleryImages("invalid")
 * // => []
 */
export function normalizeGalleryImages(images) {
  // LLM:METADATA
  // :hierarchy: [Domain:Gallery | Data_Normalization | Function]
  // :rationale: "Normalize various backend response formats to consistent array. Handles array (already correct), object map (legacy format), and invalid data."
  // :contract: pre: "images can be array, object, or invalid", post: "always returns array", invariant: "output is array"
  // :event: GALLERY_IMAGES_NORMALIZED
  // LLM:END

  // Case 1: Already an array - return as-is
  if (Array.isArray(images)) {
    return images
  }

  // Case 2: Object map keyed by image_id - extract values
  if (images && typeof images === "object" && !Array.isArray(images)) {
    return Object.values(images)
  }

  // Case 3: Invalid/missing data - return empty array
  return []
}
